/** Same file as `public/fonts/pretendard` (HTML preload / @font-face). */
const PRETENDARD_SEMIBOLD_URL = `${process.env.PUBLIC_URL || ''}/fonts/pretendard/Pretendard-SemiBold.woff2`;

/**
 * Canvas 2D 전용 Pretendard — CSS `Pretendard`와 분리해 QR·캔버스에만 SemiBold(600)를 바인딩한다.
 */
export const QR_CANVAS_FONT_FAMILY = 'PretendardQR';
export const QR_CANVAS_FONT_WEIGHT = 600;

let pretendardQRFontPromise: Promise<void> | null = null;

async function registerPretendardQRFont(): Promise<void> {
  const response = await fetch(PRETENDARD_SEMIBOLD_URL);
  if (!response.ok) {
    throw new Error(`Pretendard SemiBold load failed: ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  const face = new FontFace(QR_CANVAS_FONT_FAMILY, buffer, {
    weight: String(QR_CANVAS_FONT_WEIGHT),
    style: 'normal',
  });
  const loaded = await face.load();
  if (loaded.status !== 'loaded') {
    throw new Error(`Pretendard SemiBold FontFace status: ${loaded.status}`);
  }
  document.fonts.add(loaded);
}

/** Canvas는 반드시 load·ready 후 그린다 */
export async function ensurePretendardQRForCanvas(fontSize: number): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts?.load) return;

  if (!pretendardQRFontPromise) {
    pretendardQRFontPromise = registerPretendardQRFont();
  }
  await pretendardQRFontPromise;

  const spec = `${QR_CANVAS_FONT_WEIGHT} ${fontSize}px "${QR_CANVAS_FONT_FAMILY}"`;
  await document.fonts.load(spec);
  await document.fonts.ready;
}

export function qrCanvasFontCss(fontSize: number): string {
  return `${QR_CANVAS_FONT_WEIGHT} ${fontSize}px "${QR_CANVAS_FONT_FAMILY}"`;
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * FontFace가 Canvas에 안 붙을 때 — GlobalStyle Pretendard(600)로 DOM 레이아웃 후 SVG→Image→drawImage
 */
async function drawTextViaDomSvg(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  topY: number,
  fontSize: number,
  color: string,
): Promise<void> {
  const probe = document.createElement('span');
  probe.textContent = text;
  probe.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'font-family:Pretendard,sans-serif',
    `font-weight:${QR_CANVAS_FONT_WEIGHT}`,
    `font-size:${fontSize}px`,
    'white-space:nowrap',
  ].join(';');
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  document.body.removeChild(probe);

  const pad = 2;
  const w = Math.ceil(rect.width) + pad * 2;
  const h = Math.ceil(rect.height) + pad * 2;
  const safeText = escapeSvgText(text);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
<foreignObject width="100%" height="100%">
<div xmlns="http://www.w3.org/1999/xhtml" style="font-family:Pretendard,sans-serif;font-weight:${QR_CANVAS_FONT_WEIGHT};font-size:${fontSize}px;color:${color};line-height:1;display:flex;align-items:center;justify-content:center;width:100%;height:100%;box-sizing:border-box;padding:${pad}px;text-align:center;">
${safeText}
</div>
</foreignObject>
</svg>`;

  const img = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('QR username SVG render failed'));
      img.src = url;
    });
    ctx.drawImage(img, centerX - w / 2, topY, w, h);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function isPretendardAppliedOnCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  targetFont: string,
): boolean {
  ctx.save();
  ctx.font = `${QR_CANVAS_FONT_WEIGHT} ${fontSize}px serif`;
  const serifWidth = ctx.measureText(text).width;
  ctx.font = targetFont;
  const targetWidth = ctx.measureText(text).width;
  ctx.restore();
  return Math.abs(targetWidth - serifWidth) > 0.5;
}

/**
 * 사용자명 — QR 캔버스 가로 중앙(`centerX` = canvas.width / 2)에 맞춤.
 */
export async function drawQRUsernameOnCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  topY: number,
  fontSize: number,
  color: string,
): Promise<void> {
  await ensurePretendardQRForCanvas(fontSize);
  const font = qrCanvasFontCss(fontSize);

  if (isPretendardAppliedOnCanvas(ctx, text, fontSize, font)) {
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(text, centerX, topY);
    return;
  }

  await drawTextViaDomSvg(ctx, text, centerX, topY, fontSize, color);
}
